import type { L } from '../types/i18n'

export interface Faq {
  category: L
  question: L
  answer: L
}

export const faqs: Faq[] = [
  {
    category: { en: 'Appointments', es: 'Citas' },
    question: {
      en: 'Do I need an appointment, or can I just stop by?',
      es: '¿Necesito cita o puedo llegar sin avisar?',
    },
    answer: {
      en: 'Walk-ins are welcome and we do our best to fit you in the same day, especially for oil changes and quick diagnostics. Booking ahead guarantees you a bay and the shortest wait, so for brake work, suspension or A/C service we recommend scheduling.',
      es: 'Puedes llegar sin cita y haremos lo posible por atenderte el mismo día, sobre todo para cambios de aceite y diagnósticos rápidos. Reservar con anticipación te garantiza una bahía y la espera más corta, así que para frenos, suspensión o A/C recomendamos agendar.',
    },
  },
  {
    category: { en: 'Appointments', es: 'Citas' },
    question: {
      en: 'How long does a repair take?',
      es: '¿Cuánto tiempo tarda una reparación?',
    },
    answer: {
      en: 'It depends on the job. A simple oil change takes about 30 minutes. A brake system overhaul might take 2–3 hours. We give you an estimate of the time when you call or when you drop off your vehicle.',
      es: 'Depende del trabajo. Un simple cambio de aceite toma unos 30 minutos. Una revisión completa del sistema de frenos podría tomar 2–3 horas. Te damos una estimación del tiempo cuando llamas o cuando dejas tu vehículo.',
    },
  },
  {
    category: { en: 'Appointments', es: 'Citas' },
    question: {
      en: 'Can you get my car back the same day?',
      es: '¿Puedo recoger mi auto el mismo día?',
    },
    answer: {
      en: 'Most routine services — oil changes, brake jobs, tune-ups, diagnostics and A/C recharges — are completed the same day. Larger jobs like timing belts or parts we have to order may take longer, and we always tell you the realistic timeline before we start.',
      es: 'La mayoría de los servicios rutinarios — cambios de aceite, frenos, afinaciones, diagnóstico y recarga de A/C — se terminan el mismo día. Trabajos mayores como correas de distribución o piezas que debemos pedir pueden tardar más, y siempre te decimos el tiempo realista antes de empezar.',
    },
  },
  {
    category: { en: 'Appointments', es: 'Citas' },
    question: {
      en: 'Can you come to my location?',
      es: '¿Pueden venir a mi ubicación?',
    },
    answer: {
      en: 'No — we work from our shop on Pulaski Highway. You can drop your vehicle off before work or first thing in the morning, and in most cases we will have it ready by the afternoon.',
      es: 'No — trabajamos desde nuestro taller en la Carretera Pulaski. Puedes dejar tu vehículo antes del trabajo o temprano por la mañana, y en la mayoría de los casos lo tendremos listo por la tarde.',
    },
  },
  {
    category: { en: 'Diagnostics', es: 'Diagnóstico' },
    question: {
      en: 'How does your diagnostic process work?',
      es: '¿Cómo funciona su proceso de diagnóstico?',
    },
    answer: {
      en: 'We connect professional scan equipment to your vehicle, pull the codes, and then verify the actual cause with hands-on inspection and testing. Codes point us in a direction; they do not replace real diagnosis. You get a plain-language explanation of what we found and what it will cost before any repair begins.',
      es: 'Conectamos equipo de escaneo profesional a tu vehículo, leemos los códigos y luego verificamos la causa real con inspección y pruebas manuales. Los códigos nos orientan; no sustituyen un diagnóstico verdadero. Recibes una explicación en lenguaje claro de lo que encontramos y cuánto costará antes de empezar cualquier reparación.',
    },
  },
  {
    category: { en: 'Diagnostics', es: 'Diagnóstico' },
    question: {
      en: 'My check engine light is on. Is it urgent?',
      es: 'Tengo la luz de motor encendida. ¿Es urgente?',
    },
    answer: {
      en: 'A steady light usually means you can drive carefully to the shop. A flashing light means stop driving and call us — continuing can damage the catalytic converter. Either way, call (410) 942-5091 and we will advise you before you drive.',
      es: 'Una luz fija normalmente significa que puedes conducir con cuidado hasta el taller. Una luz intermitente significa que debes dejar de conducir y llamarnos — seguir puede dañar el convertidor catalítico. En cualquier caso, llama al (410) 942-5091 y te asesoramos antes de que conduzcas.',
    },
  },
  {
    category: { en: 'Pricing & Payment', es: 'Precios y Pago' },
    question: {
      en: 'Will you tell me the price before starting work?',
      es: '¿Me dirán el precio antes de empezar el trabajo?',
    },
    answer: {
      en: 'Always. We explain the problem, walk you through your options, and give you a price up front. Nothing gets done without your approval, and if we discover something additional mid-repair, we stop and call you.',
      es: 'Siempre. Explicamos el problema, repasamos tus opciones y te damos un precio por adelantado. Nada se hace sin tu aprobación, y si encontramos algo adicional durante la reparación, nos detenemos y te llamamos.',
    },
  },
  {
    category: { en: 'Pricing & Payment', es: 'Precios y Pago' },
    question: {
      en: 'What payment methods do you accept?',
      es: '¿Qué formas de pago aceptan?',
    },
    answer: {
      en: 'We accept cash and all major credit and debit cards. Payment is due when you pick up the vehicle, and you receive an itemized invoice listing parts and labor separately.',
      es: 'Aceptamos efectivo y todas las tarjetas de crédito y débito principales. El pago se realiza al recoger el vehículo y recibes una factura detallada con piezas y mano de obra por separado.',
    },
  },
  {
    category: { en: 'Parts & Warranty', es: 'Piezas y Garantía' },
    question: {
      en: 'Do you stand behind your work?',
      es: '¿Respaldan su trabajo?',
    },
    answer: {
      en: 'Yes. Our repairs are backed by a parts and labor warranty — the exact coverage depends on the part and manufacturer, and we tell you what it is in writing on your invoice. If something is not right after a repair, bring it back and we will make it right.',
      es: 'Sí. Nuestras reparaciones están respaldadas por una garantía de piezas y mano de obra — la cobertura exacta depende de la pieza y del fabricante, y te la indicamos por escrito en tu factura. Si algo no queda bien después de una reparación, tráelo de vuelta y lo corregimos.',
    },
  },
  {
    category: { en: 'Parts & Warranty', es: 'Piezas y Garantía' },
    question: {
      en: 'What kind of parts do you use?',
      es: '¿Qué tipo de piezas utilizan?',
    },
    answer: {
      en: 'We use quality OE or reputable aftermarket parts appropriate for your vehicle and how you drive it. If there is a meaningful price difference between options, we explain the tradeoff and let you choose.',
      es: 'Usamos piezas originales de calidad o de marcas alternativas confiables, apropiadas para tu vehículo y tu forma de conducir. Si hay una diferencia de precio importante entre opciones, te explicamos el pro y el contra y tú decides.',
    },
  },
  {
    category: { en: 'Parts & Warranty', es: 'Piezas y Garantía' },
    question: {
      en: 'Do you use eco-friendly products?',
      es: '¿Utilizan productos ecológicos?',
    },
    answer: {
      en: 'We use professional-grade products that are safe for your vehicle and the environment, and all used fluids are disposed of properly according to Maryland regulations.',
      es: 'Utilizamos productos de grado profesional que son seguros para tu vehículo y el medio ambiente, y todos los fluidos usados se desechan correctamente de acuerdo con las regulaciones de Maryland.',
    },
  },
  {
    category: { en: 'Vehicle Care', es: 'Cuidado del Vehículo' },
    question: {
      en: 'How often should I change my oil?',
      es: '¿Cada cuánto debo cambiar el aceite?',
    },
    answer: {
      en: 'For most modern vehicles running full synthetic, every 5,000–7,500 miles is a safe interval. Short trips, towing, and stop-and-go driving shorten it. Check your owner’s manual, or ask us and we will set the right interval for your car.',
      es: 'Para la mayoría de los vehículos modernos con aceite sintético, cada 5,000–7,500 millas es un intervalo seguro. Los viajes cortos, remolcar y el tráfico pesado lo acortan. Revisa tu manual del propietario o pregúntanos y definimos el intervalo correcto para tu auto.',
    },
  },
  {
    category: { en: 'Vehicle Care', es: 'Cuidado del Vehículo' },
    question: {
      en: 'When should I have my brakes checked?',
      es: '¿Cuándo debo revisar los frenos?',
    },
    answer: {
      en: 'Have them inspected at least once a year, or right away if you hear squealing or grinding, feel a pulsing pedal, or notice the car pulling to one side when stopping. Brake inspections are quick and catching wear early keeps the repair cheaper.',
      es: 'Revísalos al menos una vez al año, o de inmediato si escuchas chillidos o rechinidos, sientes el pedal pulsar o notas que el auto se va a un lado al frenar. Las inspecciones de frenos son rápidas y detectar el desgaste a tiempo abarata la reparación.',
    },
  },
  {
    category: { en: 'Vehicle Care', es: 'Cuidado del Vehículo' },
    question: {
      en: 'When does a timing belt need to be replaced?',
      es: '¿Cuándo se debe cambiar la correa de distribución?',
    },
    answer: {
      en: 'Most manufacturers specify between 60,000 and 100,000 miles. It is maintenance you do not want to postpone — a snapped belt on an interference engine can destroy the motor. We will check your vehicle’s specific interval for you.',
      es: 'La mayoría de los fabricantes indican entre 60,000 y 100,000 millas. Es un mantenimiento que no conviene posponer — una correa rota en un motor de interferencia puede destruirlo. Revisamos el intervalo específico de tu vehículo por ti.',
    },
  },
  {
    category: { en: 'Service', es: 'Servicio' },
    question: {
      en: '¿Hablan español?',
      es: '¿Hablan español?',
    },
    answer: {
      en: 'Sí. Our team is bilingual and we are glad to explain your vehicle’s diagnosis and estimate in Spanish, without rushing and without the jargon.',
      es: 'Sí. Nuestro equipo es bilingüe y con gusto le explicamos el diagnóstico y el presupuesto de su vehículo en español, sin prisas y sin tecnicismos.',
    },
  },
]
